import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/feature/SEO';
import ScrollTopButton from '@/components/feature/ScrollTopButton';
import { reviews, aboutStats } from '@/mocks/manga';

export default function AboutPage() {
  const { t } = useTranslation();

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: t('about.title'),
    description: t('about.brand.description'),
    url: 'https://www.benziwz.com/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: t('site.name'),
    url: 'https://www.benziwz.com/',
    description: t('about.brand.description'),
    email: t('about.contact.emailValue'),
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Tokyo',
      addressCountry: 'JP',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${t('about.title')} | ${t('site.name')}`}
        description={t('about.brand.description')}
        canonical="https://www.benziwz.com/about"
        jsonLd={[aboutSchema, orgSchema]}
      />
      <main>
        <Navbar />

        <section className="pt-24 pb-10 md:pb-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-xl mx-auto">
            <Breadcrumb items={[{ label: t('about.title') }]} />
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mt-4">
              {t('about.title')}
            </h1>
          </div>
        </section>

        <section className="pb-14 md:pb-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-14 md:mb-20">
              {aboutStats.map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-xl p-5 md:p-6 text-center border border-background-200/60">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <div className="text-xl md:text-2xl font-heading text-primary-600 font-semibold">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-14 md:mb-20">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-4">
                  {t('about.brand.title')}
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {t('about.brand.description')}
                </p>
                <div className="space-y-4">
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-flag-line text-primary-600"></i>
                      </span>
                      {t('about.brand.mission')}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {t('about.brand.missionText')}
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-eye-line text-accent-600"></i>
                      </span>
                      {t('about.brand.vision')}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {t('about.brand.visionText')}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-4">
                  {t('about.contact.title')}
                </h2>
                <p className="text-sm text-foreground-500 mb-6">
                  {t('about.contact.subtitle')}
                </p>
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/60 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">{t('about.contact.email')}</div>
                      <a href="mailto:contact@benziwz.com" className="text-sm text-foreground-900 hover:text-primary-600 transition-colors">
                        {t('about.contact.emailValue')}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-qq-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">{t('about.contact.qq')}</div>
                      <span className="text-sm text-foreground-900">{t('about.contact.qqValue')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">{t('about.contact.wechat')}</div>
                      <span className="text-sm text-foreground-900">{t('about.contact.wechatValue')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">{t('about.contact.address')}</div>
                      <span className="text-sm text-foreground-900">{t('about.contact.addressValue')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6 text-center">
                {t('about.reputation.title')}
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                {t('about.reputation.subtitle')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {reviews.map((review) => (
                  <div key={review.id} className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center font-heading text-sm font-semibold">
                        {review.username.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{review.username}</div>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: review.rating }, (_, i) => (
                            <span key={i} className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                    <div className="text-xs text-foreground-400 mt-3">{review.date}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <ScrollTopButton />
      </main>
    </>
  );
}